/**
 * Borrador de venta ("stand by") — la venta a crédito o al contado a medio
 * llenar se guarda sola en el navegador para poder salir de la pantalla y
 * retomarla después.
 *
 * - Un borrador por usuario y tipo de venta ("credito" / "contado" / "xmayor" /
 *   "conduce"), en
 *   localStorage. Vence a las 24 horas y el cierre de sesión lo borra
 *   (localStorage.clear() en el menú).
 * - Solo vive en ESTE navegador: no se comparte entre equipos ni usuarios.
 * - Mientras hay un borrador ofrecido sin decidir (Continuar / Descartar) el
 *   guardado automático se pausa, para no pisarlo con un formulario vacío.
 * - Con el formulario vacío (tras guardar la venta o "Nuevo") el borrador se
 *   elimina solo.
 *
 * El chasis NO queda reservado: quien restaura debe volver a validarlo.
 */
import { useEffect, useRef, useState } from "react";
import { useMaterialUIController } from "context";

const PREFIJO = "borradorventa";
export const VIGENCIA_BORRADOR_MS = 24 * 60 * 60 * 1000;
const ESPERA_GUARDADO_MS = 700;

const clave = (tipo, usuario) => `${PREFIJO}:${tipo}:${usuario}`;

// { guardado (ms), resumen: { cliente, chasis }, datos } o null. Los vencidos
// o ilegibles se eliminan al leerlos.
export function leerBorrador(tipo, usuario) {
  if (!usuario) return null;
  try {
    const raw = localStorage.getItem(clave(tipo, usuario));
    if (!raw) return null;
    const b = JSON.parse(raw);
    if (!b || !b.datos || !(Date.now() - Number(b.guardado) < VIGENCIA_BORRADOR_MS)) {
      localStorage.removeItem(clave(tipo, usuario));
      return null;
    }
    return b;
  } catch {
    return null;
  }
}

export function guardarBorrador(tipo, usuario, datos, resumen) {
  if (!usuario) return;
  try {
    localStorage.setItem(
      clave(tipo, usuario),
      JSON.stringify({ guardado: Date.now(), resumen: resumen || {}, datos })
    );
  } catch {
    /* almacenamiento lleno o bloqueado: la venta sigue, solo sin borrador */
  }
}

export function borrarBorrador(tipo, usuario) {
  if (!usuario) return;
  try {
    localStorage.removeItem(clave(tipo, usuario));
  } catch {
    /* noop */
  }
}

export function haceCuanto(ms) {
  const min = Math.round((Date.now() - Number(ms)) / 60000);
  if (min < 1) return "hace un momento";
  if (min < 60) return `hace ${min} min`;
  const h = Math.floor(min / 60);
  return h === 1 ? "hace 1 hora" : `hace ${h} horas`;
}

export function textoResumenBorrador(resumen) {
  const r = resumen || {};
  // detalle: texto libre en lugar del chasis (p. ej. "3 chasis" en x mayor).
  const queVende = r.detalle || (r.chasis ? `chasis ${r.chasis}` : "");
  return [r.cliente, queVende].filter(Boolean).join(" · ") || "sin cliente ni chasis";
}

/**
 * Guardado automático del formulario y oferta de retomarlo.
 *
 * @param tipo           "credito" | "contado" | "xmayor" | "conduce"
 * @param datos          Estado serializable del formulario (lo que se guarda).
 * @param resumen        { cliente, chasis, detalle? } para el aviso.
 * @param conDatos       false = formulario vacío: no hay nada que guardar.
 * @param restaurar      (datos) => void. Repone el estado y revalida el chasis.
 * @param autoRestaurar  true = restaurar sin preguntar al montar (el usuario ya
 *                       eligió "Continuar" en la pantalla de Ventas).
 * @param pausado        true = no guardar ni borrar todavía (la pantalla aún
 *                       carga o está restaurando: el formulario vacío de ese
 *                       momento no debe pisar el borrador).
 * @returns { oferta, continuar, descartar }: oferta es el borrador pendiente
 *          de decisión (o null).
 */
export function useBorradorVenta({
  tipo,
  datos,
  resumen,
  conDatos,
  restaurar,
  autoRestaurar = false,
  pausado = false,
}) {
  const [{ usuarioLog }] = useMaterialUIController();
  const [oferta, setOferta] = useState(null);
  const [revisado, setRevisado] = useState(false);

  const restaurarRef = useRef(restaurar);
  restaurarRef.current = restaurar;
  const actualRef = useRef(null);
  actualRef.current = { datos, resumen, conDatos, usuario: usuarioLog };

  // Al montar (o cuando la sesión termina de cargar tras recargar la página).
  useEffect(() => {
    if (!usuarioLog) return;
    const b = leerBorrador(tipo, usuarioLog);
    if (b && autoRestaurar) {
      restaurarRef.current(b.datos);
      setOferta(null);
    } else {
      setOferta(b);
    }
    setRevisado(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tipo, usuarioLog]);

  const activo = revisado && !oferta && !!usuarioLog && !pausado;
  const activoRef = useRef(activo);
  activoRef.current = activo;

  const persistir = () => {
    const a = actualRef.current;
    if (a.conDatos) guardarBorrador(tipo, a.usuario, a.datos, a.resumen);
    else borrarBorrador(tipo, a.usuario);
  };

  const serial = JSON.stringify(datos);
  useEffect(() => {
    if (!activo) return undefined;
    const t = setTimeout(persistir, ESPERA_GUARDADO_MS);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activo, serial]);

  // Salir antes de que corra la espera (Volver, cerrar el diálogo) no debe
  // perder lo último digitado.
  useEffect(
    () => () => {
      if (activoRef.current) persistir();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const continuar = () => {
    if (!oferta) return;
    restaurarRef.current(oferta.datos);
    setOferta(null);
  };

  const descartar = () => {
    borrarBorrador(tipo, usuarioLog);
    setOferta(null);
  };

  return { oferta, continuar, descartar };
}
